import moment from 'moment-timezone';

// =============================================================================
// campaignDateRange — the default window every Campaign Details date picker
// opens on:
//
//     campaign flight start  →  today
//
// WHY THE FLIGHT START, not the data extent.
// The Display and Transactions tabs have provider / campaign sub-tabs that
// re-scope the underlying data. Anchoring to the data extent meant the default
// moved every time the user switched: "All" delivered from Jul 13, one provider
// only from Jul 14, so the picker jumped. The flight window is a property of
// the campaign — it does not change with the slice being viewed, so the default
// stays put.
//
// WHY TODAY, not the flight end.
// A live campaign's flight runs into the future; ending there paints an empty
// tail of days that cannot have data yet.
//
// The end is capped so it never runs past the last day that could hold data.
// For a campaign that already ended, that is the flight end — or the last data
// day, when attribution lands transactions after the flight closes. Without
// that cap, a campaign that ended in February would open on a six-month window
// of empty days.
//
// Everything is UTC. Flight dates are stored UTC-anchored (`…T00:00:00.000Z` /
// `…T23:59:59.999Z`) and the charts bucket in UTC, so reading them in the
// viewer's zone would shift the window a day for anyone off UTC — see
// project_summary_utc_pin for the wider tz contract.
// =============================================================================

// Explicit formats rather than the lenient constructor, which emits a
// deprecation warning on unrecognised strings.
const parseUtcDay = (value) => {
  if (value === null || value === undefined || value === '') return null;
  if (moment.isMoment(value)) return value.isValid() ? value.clone().utc() : null;
  if (value instanceof Date) {
    const fromDate = moment.utc(value);
    return fromDate.isValid() ? fromDate : null;
  }
  const parsed = moment.utc(value, [moment.ISO_8601, 'YYYY-MM-DD'], false);
  return parsed.isValid() ? parsed : null;
};

const latest = (moments) => (moments.length ? moment.max(moments) : null);
const earliest = (moments) => (moments.length ? moment.min(moments) : null);

/**
 * @param {{startDate: *, endDate: *}|null} campaignOrder the campaign's flight
 *   window, as configured on the order. Absent for scopes with no order-level
 *   dates (e.g. a single provider IO node).
 * @param {Array<*>} dataDates every dated row in scope — pass `rows.map(r => r.date)`
 *   or `rows.map(r => r.xAxis)`. Used to extend past the flight end for
 *   attribution tails, and as the whole range when there is no flight window.
 * @param {{today?: *}} [options] `today` is injectable for tests; defaults to
 *   the current UTC day.
 * @returns {[moment.Moment, moment.Moment]|null} null when neither source
 *   yields a usable range, so callers can leave existing state untouched.
 */
export const resolveCampaignDateRange = (campaignOrder, dataDates = [], options = {}) => {
  const today = (parseUtcDay(options.today) || moment.utc()).clone().startOf('day');

  const dataDays = (Array.isArray(dataDates) ? dataDates : [])
    .map(parseUtcDay)
    .filter(Boolean)
    .map((m) => m.startOf('day'));

  const flightStart = parseUtcDay(campaignOrder && campaignOrder.startDate);
  const flightEnd = parseUtcDay(campaignOrder && campaignOrder.endDate);

  // No flight window — fall back to the data's own extent.
  if (!flightStart || !flightEnd) {
    const start = earliest(dataDays);
    const end = latest(dataDays);
    if (!start || !end) return null;
    return [start.clone().startOf('day'), end.clone().startOf('day')];
  }

  const start = flightStart.clone().startOf('day');

  // The last day that could hold data: the flight end, or beyond it when
  // attribution has already produced later rows.
  const lastDataDay = latest(dataDays);
  const lastMeaningfulDay =
    lastDataDay && lastDataDay.isAfter(flightEnd, 'day')
      ? lastDataDay.clone()
      : flightEnd.clone();

  // Today, but never past the last day that could hold data.
  let end = today.isBefore(lastMeaningfulDay, 'day') ? today.clone() : lastMeaningfulDay;
  end = end.startOf('day');

  // A campaign that has not started yet would otherwise invert the range.
  if (end.isBefore(start, 'day')) end = start.clone();

  return [start, end];
};

export default resolveCampaignDateRange;
